import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { parsePhoneNumber } from 'libphonenumber-js';
import { pick } from 'lodash-es';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent, formInput } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { DatatableColumn, FetchData, RktTableComponent } from '@rocketfinancialcorp/rocket-ui/table';

import { LimitEditItemFieldComponent } from '@shared/components/limits/limit-edit-item-field/limit-edit-item-field.component';
import { MESSAGE } from '@shared/constants';
import {
  AddTeamMemberParams,
  FormModel,
  PermissionRoles,
  RoleOption,
  TeamMember,
  TeamMemberFormModalParams,
  TeamMemberList,
  TeamMemberProfile,
  TeamMemberRaw,
} from '@shared/models';
import { TransactionLimitItem } from '@shared/models/transaction/transaction-limits';
import { NoteService, NotificationService, RoleService, TeamMemberService } from '@shared/services';
import { TransactionLimitsService } from '@shared/services/transaction/transaction-limits.service';
import { fromAuth } from '@shared/store';
import { ErrorUtils, safeJSONparse } from '@shared/utils';
import { emptySpacesValidator } from '@shared/validators';

import { formFields } from './form-fields';
import { NoteFormItemComponent } from '../../notes/note-form-item/note-form-item.component';

@Component({
  selector: 'app-team-member-edit-form',
  templateUrl: './team-member-edit-form.component.html',
  imports: [
    RktButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    RktFormComponent,
    NoteFormItemComponent,
    TitleCasePipe,
    DecimalPipe,
    RktIconComponent,
    RktTableComponent,
    LimitEditItemFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class TeamMemberEditFormComponent implements OnInit, AfterViewChecked, OnDestroy {
  store = inject(Store);

  @Input() teamMember!: TeamMember;

  actionType?: 'add' | 'edit';

  submitError = '';

  userExistError = false;

  userAlreadyLinked = false;

  existingTeamMemberId?: string;

  addMode: 'create' | 'search' = 'create';

  searchEmail = new FormControl('');

  searchForm!: FormGroup;

  searchFormFields = [
    formInput({
      key: 'email',
      label: 'Search by Email',
      props: { type: 'text', placeholder: 'Enter email address' },
      validators: {
        minlength: {
          expression: (control: AbstractControl) => !control.value || control.value.length >= 3,
          message: 'Minimum 3 characters required',
        },
      },
    }),
  ];

  searchResults: (TeamMemberRaw & { isAlreadyLinked: boolean })[] = [];

  hasSearched = false;

  isSearching = false;

  isLoading = false;

  searchTableColumns: DatatableColumn[] = [
    { name: 'Name', prop: 'displayName', flexSize: 2 },
    { name: 'Email', prop: 'primaryEmail', flexSize: 3 },
    {
      name: ' ',
      prop: 'actionButton',
      fixedSize: 120,
      colType: 'button',
      iconName: 'link',
      colAlign: 'center',
      colTitle: '',
    },
  ];

  searchPage = 0;
  searchPageSize = 10;
  totalSearchResults = 0;

  linkedEmployeeIds = new Set<string>();
  private currentTeamMemberIds = new Set<string>();
  private currentTeamMembersLoaded = false;

  public editForm = new FormGroup({});

  public profileModel: FormModel<TeamMemberProfile> = {
    firstName: undefined,
    middleName: undefined,
    lastName: undefined,
  };

  public formFields = formFields();

  roleOptions: RoleOption[] = [];

  note = new FormControl('', [emptySpacesValidator]);

  baLimit: number | null = null;

  baCurrency = 'USD';

  baLimitLoaded = false;

  limitValue: number | undefined = undefined;

  hasLimitValueError = false;

  private destroy$ = new Subject<void>();

  @Output() saved = new EventEmitter<Partial<AddTeamMemberParams>>();

  @Output() changed = new EventEmitter<boolean>();

  @Output() saveAllowed = new EventEmitter<boolean>();

  constructor(
    public ref: ChangeDetectorRef,
    private roleService: RoleService,
    private noteService: NoteService,
    private teamMemberService: TeamMemberService,
    private notificationService: NotificationService,
    private limitsService: TransactionLimitsService,
    public activeModal: ActiveModal,
  ) {}

  get shouldShowLimitField(): boolean {
    return (
      this.actionType === 'add' &&
      !!this.profileModel.permissionRoles &&
      this.profileModel.permissionRoles !== 'Owner' &&
      this.profileModel.permissionRoles !== 'View Only'
    );
  }

  public ngOnInit(): void {
    if (this.actionType === 'add') {
      this.fetchBALimits();
    }

    this.initEditForm();
    this.initSearchForm();
  }

  ngAfterViewChecked() {
    this.ref.detectChanges();
  }

  public ngOnDestroy(): void {
    this.editForm.markAsUntouched();
    this.destroy$.next();
    this.destroy$.complete();
  }

  modalInitData(data: TeamMemberFormModalParams): void {
    const { teamMember, actionType = 'add' } = data;

    this.actionType = actionType;

    if (actionType === 'edit' && teamMember) {
      const { firstName, middleName, lastName, title, roles, primaryEmail, workPhoneNumberExtension, workPhone, primaryPhone } = teamMember;

      this.teamMember = teamMember;

      this.profileModel = {
        firstName,
        middleName,
        lastName,
        title,
        permissionRoles: (roles?.[0]?.name as PermissionRoles) || undefined,
        email: primaryEmail,
        workPhoneNumber: workPhone?.number || undefined,
        workPhoneNumberExtension,
        mobileNumber: primaryPhone?.number || undefined,
      };

      if (this.formFields[2]?.fieldGroup[0]?.props) {
        this.formFields[2].fieldGroup[0].props.disabled = true;
      }
    }
  }

  initEditForm() {
    this.roleService
      .getRoleOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roleOptions) => {
          if (this.formFields[1]?.fieldGroup[1]?.props) {
            this.formFields[1].fieldGroup[1].props.options = roleOptions;
          }

          this.roleOptions = roleOptions;
        },
        error: (error) => {
          ErrorUtils.catchError('roleService.getRoleOptions error', error);
        },
      });

    this.editForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ref.detectChanges();
    });
  }

  fetchBALimits() {
    this.limitsService
      .getBusinessAccountsTransactionLimits()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (limits: TransactionLimitItem[]) => {
          const singleLimit = limits.find((limit: TransactionLimitItem) => limit.type === 'single');
          if (singleLimit) {
            this.baLimit = singleLimit.amount;
            this.baCurrency = singleLimit.currency || 'USD';
          }
          this.baLimitLoaded = true;
        },
        error: (error) => {
          ErrorUtils.catchError('limitsService.getBusinessAccountsTransactionLimits error', error);
          this.baLimitLoaded = true;
        },
      });
  }

  initSearchForm() {
    this.searchForm = new FormGroup({
      email: this.searchEmail,
    });

    this.searchEmail.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe((value) => {
      if (value && value.length > 2) {
        this.searchUser();
      } else if (!value || value.length === 0) {
        this.searchResults = [];
        this.hasSearched = false;
      } else {
        this.userExistError = false;
        this.userAlreadyLinked = false;
      }
    });
  }

  public onNoteFieldBlur(): void {
    if (this.note.value) {
      this.note.patchValue(this.note.value.trim());
    }
  }

  setLimitAmountValue(value: string | number | null | undefined): void {
    if (this.submitError) {
      this.submitError = '';
    }
    if (value === null || value === undefined || value === '') {
      this.limitValue = undefined;
      this.profileModel.singleTransactionLimit = this.limitValue;
      return;
    }
    const cleanValue = typeof value === 'string' ? value.replace(/,/g, '') : String(value);
    const parsedValue = parseFloat(cleanValue);
    this.limitValue = isNaN(parsedValue) ? undefined : parsedValue;

    this.profileModel.singleTransactionLimit = this.limitValue;
  }

  onLimitAmountValueError(isError: boolean): void {
    this.hasLimitValueError = isError;
  }

  getTeamMemberData() {
    const {
      firstName,
      lastName,
      middleName = '',
      title,
      permissionRoles,
      workPhoneNumber,
      workPhoneNumberExtension,
      mobileNumber,
      email,
    } = this.profileModel;

    const workPhoneParsed = workPhoneNumber ? parsePhoneNumber(workPhoneNumber).number.toString() : undefined;
    const mobilePhoneParsed = mobileNumber ? parsePhoneNumber(mobileNumber).number.toString() : undefined;

    const teamMember = pick(this.teamMember, [
      'firstName',
      'lastName',
      'middleName',
      'prefix',
      'suffix',
      'title',
      'department',
      'sex',
      'gender',
      'pronoun',
      'employeeNumber',
    ]);

    const teamMemberData: Partial<AddTeamMemberParams> = {
      ...teamMember,
      firstName: firstName!,
      lastName: lastName!,
      middleName: middleName || undefined,
      title: title || undefined,
      workPhoneNumber: workPhoneParsed,
      workPhoneNumberExtension: workPhoneNumberExtension || undefined,
      mobileNumber: mobilePhoneParsed,
    };

    if (
      this.actionType === 'add' &&
      permissionRoles !== 'Owner' &&
      permissionRoles !== 'View Only' &&
      this.limitValue !== undefined &&
      this.limitValue !== null
    ) {
      teamMemberData.limits = {
        SINGLE: { amount: this.limitValue },
      };
    }

    const selectedRole = this.roleOptions.find((role) => role.value === permissionRoles);
    const roleObj = selectedRole
      ? {
          id: selectedRole.id,
          name: selectedRole.value,
        }
      : undefined;

    teamMemberData.roles = roleObj ? [roleObj] : undefined;

    return { teamMemberData, email };
  }

  public onSave(): void {
    this.editForm.markAllAsTouched();
    this.submitError = '';

    if (this.shouldShowLimitField && this.hasLimitValueError) {
      return;
    }

    if (this.editForm.invalid) {
      return;
    }

    const { teamMemberData, email } = this.getTeamMemberData();

    if (this.actionType === 'edit') {
      this.updateTeamMember(teamMemberData);
    } else {
      this.checkIfTeamMemberExist({ ...teamMemberData, email });
    }
  }

  resetForm() {
    this.userExistError = false;
    this.userAlreadyLinked = false;
    this.submitError = '';
    this.searchResults = [];
    this.hasSearched = false;
    this.existingTeamMemberId = undefined;
    this.searchPage = 0;
    this.totalSearchResults = 0;
    this.linkedEmployeeIds.clear();
    this.currentTeamMemberIds.clear();
    this.currentTeamMembersLoaded = false;

    this.profileModel = {
      ...this.profileModel,
      firstName: undefined,
      middleName: undefined,
      lastName: undefined,
      title: undefined,
      workPhoneNumber: undefined,
      workPhoneNumberExtension: undefined,
      mobileNumber: undefined,
      email: undefined,
      permissionRoles: undefined,
    };

    this.editForm.markAsUntouched();
    this.note.setValue('');
    this.searchEmail.setValue('');
  }

  searchUser() {
    if (!this.searchEmail.value || this.searchEmail.value.length <= 2) {
      return;
    }
    this.searchEmployees({ page: 0, size: this.searchPageSize });
  }

  searchEmployees({ page, size }: FetchData) {
    if (!this.searchEmail.value || this.searchEmail.value.length <= 2) {
      return;
    }

    this.isSearching = true;
    this.userExistError = false;
    this.userAlreadyLinked = false;
    this.searchPage = page ?? 0;

    const externalSearch$ = this.teamMemberService.checkIfTeamMemberExist(this.searchEmail.value!, page ?? 0, size || this.searchPageSize);

    const getCurrentTeamMembers$ = this.currentTeamMembersLoaded
      ? of({ items: [], totalElements: 0, totalPages: 0, pageNumber: 0, pageSize: 0 } as TeamMemberList)
      : this.teamMemberService.getBusinessAccountTeamMembers({
          page: 0,
          size: 1000,
          searchString: this.searchEmail.value,
        });

    externalSearch$
      .pipe(
        switchMap((existingTeamMembers) =>
          getCurrentTeamMembers$.pipe(map((currentTeamMembers) => ({ existingTeamMembers, currentTeamMembers }))),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ existingTeamMembers, currentTeamMembers }) => {
          let activeBusinessAccountId: string | null = null;
          this.store
            .select(fromAuth.selectBusinessAccountId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((id) => {
              activeBusinessAccountId = id;
            });

          if (currentTeamMembers && !this.currentTeamMembersLoaded) {
            this.currentTeamMemberIds = new Set(currentTeamMembers.items.map((member: TeamMember) => member.id));
            this.currentTeamMembersLoaded = true;
          }

          if (existingTeamMembers?.content?.length) {
            this.searchResults = existingTeamMembers.content.map((member: TeamMemberRaw) => {
              const isAlreadyLinked =
                member.businessAccountId === activeBusinessAccountId ||
                this.linkedEmployeeIds.has(member.id) ||
                this.currentTeamMemberIds.has(member.id);
              return {
                ...member,
                displayName: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
                isAlreadyLinked,
                actionButton: !isAlreadyLinked,
              };
            });
            this.totalSearchResults = existingTeamMembers.totalElements;
          } else {
            this.searchResults = [];
            this.totalSearchResults = 0;
          }

          this.isSearching = false;
          this.hasSearched = true;
        },
        error: (error) => {
          this.searchResults = [];
          this.totalSearchResults = 0;
          this.isSearching = false;
          this.hasSearched = true;
          ErrorUtils.catchError('teamMemberService.searchEmployees error', error);
        },
      });
  }

  selectUserToAdd(user: TeamMemberRaw & { isAlreadyLinked: boolean }) {
    if (user.isAlreadyLinked) {
      this.userAlreadyLinked = true;
      return;
    }

    this.existingTeamMemberId = user.id;
    this.linkEmployeeToBusinessAccount();
  }

  onTableButtonClick({ row }: { colButton: string; row: TeamMemberRaw & { isAlreadyLinked: boolean } }) {
    if (row.isAlreadyLinked || this.isLoading) {
      return;
    }
    this.selectUserToAdd(row);
  }

  public updateTeamMember(teamMemberData: Partial<AddTeamMemberParams>) {
    this.isLoading = true;
    this.submitError = '';

    this.teamMemberService
      .updateEmployee(this.teamMember.id, teamMemberData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.notificationService.displaySuccess('Team Member updated successfully.', 'Success');
          this.activeModal.close(true);
        },
        error: (error) => this.handleError(error, 'updateEmployee'),
      });
  }

  checkIfTeamMemberExist(teamMemberData: Partial<AddTeamMemberParams>) {
    this.isLoading = true;
    this.submitError = '';
    this.userExistError = false;
    this.userAlreadyLinked = false;

    this.store
      .select(fromAuth.selectBusinessAccountId)
      .pipe(
        switchMap((activeBusinessAccountId) => {
          return this.teamMemberService
            .checkIfTeamMemberExist(teamMemberData.email!)
            .pipe(map((existingTeamMembers) => ({ existingTeamMembers, activeBusinessAccountId })));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ existingTeamMembers, activeBusinessAccountId }) => {
        const existingMember = existingTeamMembers?.content?.[0];

        if (existingMember?.id) {
          this.existingTeamMemberId = existingMember.id;
          if (existingMember.businessAccountId === activeBusinessAccountId) {
            this.userAlreadyLinked = true;
          } else {
            this.userExistError = true;
          }
          this.isLoading = false;
        } else {
          this.addTeamMember(teamMemberData);
        }
      });
  }

  public addTeamMember(teamMemberData: Partial<AddTeamMemberParams>) {
    this.isLoading = true;
    this.submitError = '';
    this.userExistError = false;

    this.teamMemberService
      .addTeamMember(teamMemberData as AddTeamMemberParams)
      .pipe(
        switchMap((teamMemberResponse) => this.addNoteToTeamMember(teamMemberResponse, this.note.value)),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: ({ noteResponse }) => this.onTeamMemberCreateSuccess(!noteResponse),
        error: (error) => {
          const errorObj = safeJSONparse(error as string);
          if (errorObj.errorCode === 'entity-already-exists') {
            this.userExistError = true;
          } else {
            const submitError = ErrorUtils.parseExtendedError(error);
            this.handleError(submitError, 'addTeamMember');
          }
        },
      });
  }

  linkEmployeeToBusinessAccount() {
    if (!this.existingTeamMemberId) {
      this.submitError = 'We are unable to complete this request. Please contact your administrator.';
      return;
    }

    this.isLoading = true;
    this.submitError = '';

    this.store
      .select(fromAuth.selectBusinessAccountId)
      .pipe(
        switchMap((businessAccountId) => {
          return this.teamMemberService
            .linkEmployeeToBusinessAccount(businessAccountId, this.existingTeamMemberId!)
            .pipe(switchMap((teamMemberResponse) => this.addNoteToTeamMember(teamMemberResponse, this.note.value)));
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: ({ noteResponse }) => {
          if (!noteResponse) {
            this.notificationService.displayError('Team Member added successfully. Unable to create Note for Team Member');
          } else {
            this.notificationService.displaySuccess('Team Member added successfully.', 'Success');
          }

          if (this.existingTeamMemberId) {
            this.linkedEmployeeIds.add(this.existingTeamMemberId);
            this.currentTeamMemberIds.add(this.existingTeamMemberId);
          }

          if (this.searchEmail.value && this.hasSearched) {
            this.searchEmployees({ page: this.searchPage, size: this.searchPageSize });
          }

          this.existingTeamMemberId = undefined;

          setTimeout(() => {
            this.activeModal.close(true);
          }, 1500);
        },
        error: (error) => {
          const submitError = ErrorUtils.parseExtendedError(error);
          this.handleError(submitError, 'linkEmployeeToBusinessAccount');
        },
      });
  }

  addNoteToTeamMember(teamMemberResponse: TeamMemberRaw, note?: string | null) {
    if (!note) {
      return of({ noteResponse: true });
    }

    return this.noteService
      .addNote({
        entityId: teamMemberResponse.id,
        entityType: 'TEAM_MEMBER',
        contentText: note,
      })
      .pipe(
        catchError((error) => {
          ErrorUtils.catchError('noteService.addNote', error);
          return of(false);
        }),
        map((noteResponse) => ({ noteResponse })),
      );
  }

  onTeamMemberCreateSuccess(noteError: boolean) {
    if (noteError) {
      this.notificationService.displayError('Team Member created successfully. Unable to create Note for Team Member');
    } else {
      this.notificationService.displaySuccess('Team Member created successfully.', 'Success');
    }
    this.activeModal.close(true);
  }

  private handleError(error: string | number | HttpErrorResponse, serviceFunction: string): void {
    let errorMessage = '';

    if (typeof error === 'number') {
      if (error === HttpStatusCode.Conflict) {
        errorMessage = MESSAGE.TEAM_MEMBER_ALREADY_EXIST;
      } else if (error === HttpStatusCode.Forbidden || error === HttpStatusCode.Unauthorized) {
        errorMessage = MESSAGE.PERMISSION_DENIED;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else {
      errorMessage = MESSAGE.GENERIC_ERROR;
    }

    this.submitError = errorMessage;

    ErrorUtils.catchError(`teamMemberService.${serviceFunction}`, this.submitError);
  }
}
